import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

// ---------------------------------------------------------------------------
// Card config types
// ---------------------------------------------------------------------------

export type CardSlotType = 'property' | 'association';

export interface CardFieldSlot {
  type: CardSlotType;
  name: string;
  fallbacks?: CardFieldSlot[];
  /** When true, render the field label above the value on the card. */
  showLabel?: boolean;
}

export interface CardRow {
  slots: CardFieldSlot[];
}

export interface PipelineCardConfig {
  rows: CardRow[];
}

// ---------------------------------------------------------------------------
// Constants (module-private)
// ---------------------------------------------------------------------------

/** Maximum scalar property fields shown in the value row. */
const MAX_INLINE_FIELDS = 3;

/**
 * Field types that don't fit a single-line value row and are skipped.
 * Everything else — including text and markdown — is eligible. The card
 * truncates long values rather than expanding vertically.
 *
 * Markdown fields configured in a layout are already covered by this
 * eligibility check (removed from this set); `buildSupplementalPropertyOptions`
 * in cardConfigOptions.ts is a fallback only for fields this check would
 * otherwise skip.
 */
const SKIP_FIELD_TYPES = new Set([
  'json',
  'composite',
  'working_hours_schedule',
]);

/**
 * Names of properties already rendered elsewhere on the card (title /
 * footer / hidden-from-views) so they don't repeat in the value row.
 */
const SKIP_PROPERTY_NAMES = new Set([
  'ahoy_display_name',
  'ahoy_name',
  'ahoy_updated_at',
  'ahoy_created_at',
  'ahoy_hidden',
]);

/**
 * Priority order for value-row field selection. Lower index = higher
 * priority. Currency/number is shown first (matches the deal card's
 * amount-first emphasis), then date, then select/text, then everything
 * else. Within a tie, user-defined (`org_*`) properties win, then
 * alphabetical.
 */
const FIELD_TYPE_PRIORITY: Record<string, number> = {
  currency: 0,
  multi_currency: 0,
  number: 1,
  percentage: 1,
  date: 2,
  datetime: 2,
  select: 3,
  multi_select: 3,
  text: 4,
  phone_number: 5,
  email: 6,
  url: 7,
};

// ---------------------------------------------------------------------------
// Field selection
// ---------------------------------------------------------------------------

/**
 * Pick which entity properties to show in the value row.
 *
 * Inclusive: every single-line scalar property is eligible. Excluded:
 * properties already rendered elsewhere (title, timestamps, hidden),
 * internal-only writes, multi-value, and types that don't fit a single
 * line (json, composite, working hours).
 *
 * Ordering: user-defined (`org_*`) properties first, then field-type
 * priority, then alphabetical for stable ties. Capped at
 * `MAX_INLINE_FIELDS`.
 */
export function selectInlineFields(
  schemas: Record<string, PropertyTypeSchema>,
  maxFields: number = MAX_INLINE_FIELDS
): PropertyTypeSchema[] {
  const candidates = Object.values(schemas).filter(isInlineFieldEligible);
  candidates.sort(compareInlineFields);
  return candidates.slice(0, maxFields);
}

export function isInlineFieldEligible(schema: PropertyTypeSchema): boolean {
  if (SKIP_PROPERTY_NAMES.has(schema.property_type_name)) {
    return false;
  }
  if (SKIP_FIELD_TYPES.has(schema.field_type_name)) {
    return false;
  }
  if (schema.value_write_access === 'internal') {
    return false;
  }
  if (schema.is_multi) {
    return false;
  }
  if (schema.subproperties && schema.subproperties.length > 0) {
    return false;
  }
  return true;
}

/**
 * Whether a property is eligible for display on cards (field picker, sort).
 *
 * `value_write_access === 'internal'` means the *system* writes the value —
 * it says nothing about readability, so we don't filter on it here.
 * `isInlineFieldEligible` still excludes internal-write properties from the
 * auto-selected defaults to keep the default layout focused.
 */
export function isCardFieldPickerEligible(schema: PropertyTypeSchema): boolean {
  if (SKIP_PROPERTY_NAMES.has(schema.property_type_name)) {
    return false;
  }
  if (SKIP_FIELD_TYPES.has(schema.field_type_name)) {
    return false;
  }
  if (schema.is_multi) {
    return false;
  }
  if (schema.subproperties && schema.subproperties.length > 0) {
    return false;
  }
  return true;
}

function compareInlineFields(
  a: PropertyTypeSchema,
  b: PropertyTypeSchema
): number {
  const aCustom = a.property_type_name.startsWith('org_');
  const bCustom = b.property_type_name.startsWith('org_');
  if (aCustom !== bCustom) {
    return aCustom ? -1 : 1;
  }
  const aPriority = FIELD_TYPE_PRIORITY[a.field_type_name] ?? 99;
  const bPriority = FIELD_TYPE_PRIORITY[b.field_type_name] ?? 99;
  if (aPriority !== bPriority) {
    return aPriority - bPriority;
  }
  return (a.label || a.property_type_name).localeCompare(
    b.label || b.property_type_name
  );
}
